import React, { useCallback, useEffect, useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { reviewKeys } from '../../data/content';
import './ReviewsSection.css';

const AUTO_MS = 5000;

function StarRating({ rating }) {
  return (
    <div className="review-stars" aria-label={`${rating} sur 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={16}
          className={i < rating ? 'star-filled' : 'star-empty'}
          fill={i < rating ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );
}

function useVisibleCount() {
  const [count, setCount] = useState(3);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w <= 640) setCount(1);
      else if (w <= 992) setCount(2);
      else setCount(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return count;
}

export default function ReviewsSection({ variant = 'page' }) {
  const { t } = useLanguage();
  const visible = useVisibleCount();
  const total = reviewKeys.length;
  const maxIndex = Math.max(0, total - visible);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  }, [maxIndex]);

  const goNext = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  useEffect(() => {
    if (paused || maxIndex === 0) return undefined;
    const timer = window.setInterval(goNext, AUTO_MS);
    return () => window.clearInterval(timer);
  }, [paused, maxIndex, goNext]);

  const slidePercent = 100 / visible;
  const isEmbedded = variant === 'embedded';

  return (
    <section
      className={`section reviews-section${isEmbedded ? ' reviews-section-embedded' : ''}`}
      id={isEmbedded ? undefined : 'avis'}
    >
      <div className={isEmbedded ? undefined : 'container'}>
        <div className="section-header reviews-title-block">
          <h2>{t.reviews.title}</h2>
          {!isEmbedded && <p>{t.reviews.subtitle}</p>}
        </div>

        <div
          className="reviews-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            type="button"
            className="reviews-nav reviews-nav-prev"
            onClick={goPrev}
            aria-label="Previous reviews"
          >
            <ChevronLeft size={22} />
          </button>

          <div className="reviews-viewport">
            <div
              className="reviews-track"
              style={{
                transform: `translateX(-${index * slidePercent}%)`,
              }}
            >
              {reviewKeys.map(({ id, rating, color }) => {
                const review = t.reviews[id];
                return (
                  <article
                    key={id}
                    className="review-card"
                    style={{ flex: `0 0 ${slidePercent}%`, alignSelf: 'flex-start' }}
                  >
                    <div className="review-card-inner" style={{ gap: '10px', height: 'auto' }}>
                      <Quote size={28} className="review-quote-icon" />
                      <StarRating rating={rating} />
                      <p className="review-text" style={{ flex: '0 0 auto' }}>&ldquo;{review.text}&rdquo;</p>
                      <div className="review-footer">
                        <div
                          className="review-avatar"
                          style={{ background: color }}
                          aria-hidden="true"
                        >
                          {review.name.charAt(0)}
                        </div>
                        <div className="review-author">
                          <strong>{review.name}</strong>
                          <span>{review.role}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {review.category && <span className="review-doc">{review.category}</span>}
                        {review.subcategory && <span className="review-doc">{review.subcategory}</span>}
                        {review.doc && <span className="review-doc">{review.doc}</span>}
                        {review.from && review.to && (
                          <span className="review-doc">{review.from} → {review.to}</span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className="reviews-nav reviews-nav-next"
            onClick={goNext}
            aria-label="Next reviews"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="reviews-dots" role="tablist" aria-label="Reviews pagination">
          {Array.from({ length: maxIndex + 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`reviews-dot ${i === index ? 'active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
